import React from 'react';
import { observer } from 'src/utils/mobx-react';
import { I18n } from 'src/domains/layouts/webview/components/language/I18n';
import {
    OpenBetsBtnWrapper,
    ReAddBetslipBtnWrapper,
} from 'src/domains/sportsbook/betting/ui/betSlip/betslip/Betslip.style';
import { Amount } from 'src_common/common/amount/Amount';
import {
    BetslipReceiptHeader,
    BetslipReceiptWrapper,
    Text,
    ListElement,
    ReceiptValue,
    SmallText,
    BetslipReceiptIconWrapper,
    BetslipReceiptGiftIconWrapper,
    ListWrapper,
    BonusEngineFreeBetExcludedReturnsText,
    ButtonsWrapper,
    BetslipReceiptBottomEdge,
} from './BetslipReceipt.style';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import { useCommon } from 'src/domains/common/Common';
import { SdkCustomer } from 'src/domains/layouts/state/customer';
import { UniverseComponent } from 'src/domains/common/universes';
import { AppSportsBookState } from 'src/domains/sportsbook/state/AppSportsBook.state';

export const BetslipReceipt = observer('BetslipReceipt', () => {
    const common = useCommon();
    const appSportsBookState = AppSportsBookState.get(common);
    const configState = ConfigComponents.get(common).config;
    const { bonusEngine } = configState;
    const sdkCustomer = SdkCustomer.get(common);
    const { betSlipState } = appSportsBookState;
    const { betReceipt, setShouldKeepSelections } = betSlipState.betSlipSummaryState;

    if (betReceipt === null) {
        return null;
    }

    return (
        <BetslipReceiptWrapper
            className='betslip__content'
            key='Betslip:receipt'
            variant={configState.componentVariants.betslipVariant}
        >
            <BetslipReceiptHeader>
                <UniverseComponent>
                    {(universeVersion): React.ReactElement => <universeVersion.betReceiptLogo />}
                </UniverseComponent>
                <I18n
                    langKey='betslip.bet-receipt.header'
                    defaultText='Bet Receipt'
                />
            </BetslipReceiptHeader>
            <Text data-test='bet-receipt-paragraph'>
                <BetslipReceiptIconWrapper />
                <I18n
                    langKey='betslip.bet-receipt.description'
                    defaultText='Your Bets Been Placed'
                />
            </Text>
            <ListWrapper>
                <ListElement>
                    <span data-test='label-bet-receipt-stake'>
                        {betReceipt.isFreeBet ? (
                            <I18n
                                langKey='betslip.bet-receipt.free-bet-stake.label'
                                defaultText='Free Bet Credit Stake'
                            />
                        ) : (
                            <I18n
                                langKey='betslip.bet-receipt.stake.label'
                                defaultText='Total Stake'
                            />
                        )}
                    </span>
                    <ReceiptValue data-test='value-bet-receipt-stake'>
                        {betReceipt.isFreeBet ? <BetslipReceiptGiftIconWrapper /> : null}
                        {betReceipt.totalBonusEngineFreeBetsAmount === null
                            ? sdkCustomer.money(betReceipt.totalStake)
                            : sdkCustomer.money(betReceipt.totalBonusEngineFreeBetsAmount)}
                    </ReceiptValue>
                </ListElement>
                {betReceipt.totalCashStake === null ||
                betReceipt.totalCashStake.isLessOrEqual(new Amount('0')) ? null : (
                    <ListElement>
                        <span data-test='label-bet-receipt-cash-stake'>
                            <I18n
                                langKey='betslip.bet-receipt.cash-stake.label'
                                defaultText='Cash Stake'
                            />
                        </span>
                        <ReceiptValue data-test='value-bet-receipt-stake'>
                            {sdkCustomer.money(betReceipt.totalCashStake)}
                        </ReceiptValue>
                    </ListElement>
                )}
                <ListElement>
                    <span data-test='label-bet-receipt-total-potential-returns'>
                        {betReceipt.totalBonusEngineFreeBetsAmount === null ? (
                            <I18n
                                langKey='betslip.bet-receipt.potential-returns.label'
                                defaultText='Total Potential Returns'
                            />
                        ) : (
                            <I18n
                                langKey='betslip.bet-receipt.total-returns.label'
                                defaultText='Total Returns'
                            />
                        )}
                    </span>
                    <ReceiptValue data-test='value-bet-receipt-total-potential-returns'>
                        {betReceipt.totalPotentialReturns.isEqualWith(new Amount('0'))
                            ? 'n/a'
                            : sdkCustomer.money(betReceipt.totalPotentialReturns)}
                        {betReceipt.totalBonusEngineFreeBetsAmount === null ? '' : '*'}
                    </ReceiptValue>
                    {betReceipt.isFreeBet && !bonusEngine ? (
                        <SmallText>
                            <I18n
                                langKey='betslip.bet-receipt.return-exclude-stake.label'
                                defaultText='Return exclude stake'
                            />
                        </SmallText>
                    ) : null}
                </ListElement>
                {betReceipt.isFreeBet && !bonusEngine ? (
                    <ListElement>
                        <span data-test='label-bet-receipt-remaining-free-bet-balance'>
                            <I18n
                                langKey='betslip.bet-receipt.remaining-free-bet-balance.label'
                                defaultText='Remaining Free Bet Balance'
                            />
                        </span>
                        <ReceiptValue
                            className='receipt-value'
                            data-test='value-bet-receipt-remaining-free-bet-balance'
                        >
                            {sdkCustomer.money(
                                betSlipState.freeBetsAmount === null ? null : betSlipState.freeBetsAmount
                            )}
                        </ReceiptValue>
                    </ListElement>
                ) : null}
            </ListWrapper>

            <ButtonsWrapper>
                <OpenBetsBtnWrapper
                    isButton={true}
                    size='small'
                    onClick={undefined}
                    to={{ account: 'bet-history' }}
                    dataTest='bet-receipt-open-bets-button'
                    variant={configState.componentVariants.betslipVariant}
                >
                    <I18n
                        langKey='betslip.bet-receipt.open-bets-button.label'
                        defaultText='Open Bets'
                    />
                </OpenBetsBtnWrapper>

                <UniverseComponent>
                    {(universeModule): React.ReactElement => (
                        <ReAddBetslipBtnWrapper
                            onClick={() => setShouldKeepSelections(true)}
                            size='small'
                            appearance={universeModule.betReceiptButtons.reAddBetslipButton}
                            variant={configState.componentVariants.betslipVariant}
                        >
                            <I18n
                                langKey='betslip.bet-receipt.reuse-selections-button.label'
                                defaultText='Re-add to Betslip'
                            />
                        </ReAddBetslipBtnWrapper>
                    )}
                </UniverseComponent>
            </ButtonsWrapper>
            {betReceipt.totalBonusEngineFreeBetsAmount !== null && (
                <BonusEngineFreeBetExcludedReturnsText>
                    *
                    <I18n
                        langKey='betslip.bet-receipt.bonuse-engine.stake-excluded'
                        defaultText='Free Bet stake excluded in returns.'
                    />
                </BonusEngineFreeBetExcludedReturnsText>
            )}
            <BetslipReceiptBottomEdge variant={configState.componentVariants.betslipVariant} />
        </BetslipReceiptWrapper>
    );
});
